import { useCallback, useEffect, useState } from "react";

import { MessageChip } from "@/components/chips";
import { Pager, Select } from "@/routes/orders";
import * as messages from "@/lib/api/messages";
import { ApiError } from "@/lib/api-client";
import type {
  MessageStatus,
  MessageSummary,
  OutboundMessage,
  OutboundMessageDetail,
} from "@/lib/api/types";
import { cn, formatDateTime } from "@/lib/utils";

/**
 * Communications — every message the system has tried to send (ADMIN.md §7).
 *
 * The screen exists to answer one question that had no answer before it: *did
 * this customer get their confirmation?* Everything on it is arranged around
 * that, which is why the recipient and the order number are the two search
 * fields and there is no free-text search over anything else.
 *
 * ── The two honesty rules this screen keeps ────────────────────────────────
 *
 * **"Sent" is never called "Delivered".** SENT means ZeptoMail accepted the
 * handoff. Nothing here knows whether a human received anything until the
 * delivery webhooks in §7.5 exist, and a panel that blurred the two would have
 * support telling customers their receipt arrived when it may have bounced.
 *
 * **A log full of rows is not proof mail is working.** With no provider
 * configured every message is recorded SKIPPED — rows arriving, no failures,
 * nothing delivered. That is the failure this screen is most likely to hide,
 * so it is stated in a banner above everything else rather than left to be
 * inferred from a column of grey chips.
 */

const STATUSES: MessageStatus[] = ["QUEUED", "SENT", "DELIVERED", "FAILED", "BOUNCED", "SKIPPED"];

export function MessagesRoute() {
  const [summary, setSummary] = useState<MessageSummary | null>(null);
  const [templates, setTemplates] = useState<string[]>([]);
  const [reload, setReload] = useState(0);

  useEffect(() => {
    const controller = new AbortController();

    void messages
      .fetchMessageSummary(controller.signal)
      .then((data) => {
        if (!controller.signal.aborted) setSummary(data);
      })
      .catch(() => {
        // A failed summary must not take the table with it — same rule as the
        // payments screen. The tiles simply do not render.
      });

    void messages
      .fetchTemplates(controller.signal)
      .then((data) => {
        if (!controller.signal.aborted) setTemplates(data);
      })
      .catch(() => {
        // The dropdown falls back to "All templates" only. Harmless.
      });

    return () => controller.abort();
  }, [reload]);

  const refresh = useCallback(() => setReload((n) => n + 1), []);

  return (
    <div>
      <header className="mb-5">
        <h1 className="text-[20px] font-semibold text-ink">Communications</h1>
        <p className="mt-1 text-[13px] text-muted">
          Every message this system has tried to send — which template, to whom, and what happened.
        </p>
      </header>

      {summary ? <Summary summary={summary} /> : null}

      <Table templates={templates} onChanged={refresh} />
    </div>
  );
}

function Summary({ summary }: { summary: MessageSummary }) {
  return (
    <>
      {/*
        Above the tiles, not inside them. With no provider configured the log
        keeps filling and nothing is wrong-looking anywhere — no failures, rows
        arriving on every order — while not one customer has received anything.
        It is the only state on this screen that invalidates everything else on
        it, so it gets a banner rather than a number.
      */}
      {summary.mailEnabled ? null : (
        <p className="mb-4 rounded-lg bg-warn-tint px-4 py-3 text-[12.5px] leading-relaxed text-warn">
          <strong className="font-bold">No mail provider is configured.</strong> Messages are being
          recorded but nothing is being sent — every row below reads “Not configured”. Set{" "}
          <code>ZEPTOMAIL_TOKEN</code> and <code>ZEPTOMAIL_FROM_EMAIL</code> on the API to start
          delivering.
        </p>
      )}

      <div className="mb-5 grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
        <Tile
          label="Sent · 24h"
          value={String(summary.sent24h)}
          note={summary.lastSentAt ? `last ${formatDateTime(summary.lastSentAt)}` : "none yet"}
        />
        <Tile
          label="Failed · 24h"
          value={String(summary.failed24h)}
          {...(summary.failed24h > 0 ? { tone: "bad" as const } : {})}
        />
        <Tile label="Not configured · 24h" value={String(summary.skipped24h)} />
        {/*
          A row still QUEUED is a send that died between the log write and the
          provider call. Nothing retries it on its own, so it is worth a tile
          rather than a filter nobody would think to apply.
        */}
        <Tile
          label="Stuck in queue"
          value={String(summary.stuck)}
          {...(summary.stuck > 0 ? { tone: "warn" as const } : {})}
        />
      </div>
    </>
  );
}

function Tile({
  label,
  value,
  note,
  tone,
}: {
  label: string;
  value: string;
  note?: string;
  tone?: "warn" | "bad";
}) {
  return (
    <div className="rounded-xl border border-line bg-surface p-4">
      <p className="text-[11px] font-bold tracking-wide text-faint uppercase">{label}</p>
      <p
        className={cn(
          "mt-1 text-[18px] font-semibold tabular-nums",
          tone === "bad" ? "text-bad" : tone === "warn" ? "text-warn" : "text-ink",
        )}
      >
        {value}
      </p>
      {note ? <p className="mt-0.5 text-[11.5px] text-faint">{note}</p> : null}
    </div>
  );
}

// ─── The table ───────────────────────────────────────────────────────────────

function Table({ templates, onChanged }: { templates: string[]; onChanged: () => void }) {
  const [page, setPage] = useState(1);
  const [status, setStatus] = useState<MessageStatus | "">("");
  const [template, setTemplate] = useState("");
  const [failedOnly, setFailedOnly] = useState(false);
  const [search, setSearch] = useState("");

  const [rows, setRows] = useState<OutboundMessage[] | null>(null);
  const [pages, setPages] = useState(1);
  const [error, setError] = useState<string | null>(null);
  const [open, setOpen] = useState<string | null>(null);
  const [reload, setReload] = useState(0);

  /**
   * One box, two filters.
   *
   * An operator pastes whatever they have — an email address or an order
   * number — and expects it to work. Both are exact-match server-side, so
   * choosing between them here (rather than offering two boxes, or a substring
   * search that would drop the index) keeps the query fast and the UI honest.
   */
  const term = search.trim();
  const looksLikeOrder = /^ARW-/i.test(term);

  useEffect(() => {
    const controller = new AbortController();

    void (async () => {
      try {
        const result = await messages.fetchMessages(
          {
            page,
            status: status || undefined,
            template: template || undefined,
            failed: failedOnly ? true : undefined,
            ...(term
              ? looksLikeOrder
                ? { orderNumber: term.toUpperCase() }
                : { recipient: term }
              : {}),
          },
          controller.signal,
        );

        if (controller.signal.aborted) return;
        setRows(result.data);
        setPages(Math.max(1, result.meta.pages));
        setError(null);
      } catch (thrown) {
        if (!controller.signal.aborted) {
          setError(thrown instanceof ApiError ? thrown.message : "Could not load messages.");
        }
      }
    })();

    return () => controller.abort();
  }, [page, status, template, failedOnly, term, looksLikeOrder, reload]);

  const refresh = useCallback(() => {
    setReload((n) => n + 1);
    onChanged();
  }, [onChanged]);

  return (
    <>
      <div className="mb-4 flex flex-wrap items-center gap-2">
        <input
          type="search"
          value={search}
          onChange={(event) => {
            setSearch(event.target.value);
            setPage(1);
          }}
          placeholder="Email address or order number…"
          className="w-[280px] rounded-lg border border-line bg-surface px-3 py-1.5 text-[13px] text-ink placeholder:text-faint"
        />

        <Select
          value={status}
          onChange={(value) => {
            setStatus(value as MessageStatus | "");
            setPage(1);
          }}
          options={[
            { value: "", label: "All statuses" },
            ...STATUSES.map((s) => ({ value: s, label: s.toLowerCase() })),
          ]}
        />

        <Select
          value={template}
          onChange={(value) => {
            setTemplate(value);
            setPage(1);
          }}
          options={[
            { value: "", label: "All templates" },
            ...templates.map((name) => ({ value: name, label: messages.templateLabel(name) })),
          ]}
        />

        <label className="flex cursor-pointer items-center gap-2 text-[13px] text-body">
          <input
            type="checkbox"
            checked={failedOnly}
            onChange={(event) => {
              setFailedOnly(event.target.checked);
              setPage(1);
            }}
          />
          Failures only
        </label>
      </div>

      {term ? (
        <p className="mb-3 text-[12px] text-faint">
          Matching on {looksLikeOrder ? "order number" : "recipient"} — exactly, not partially.
        </p>
      ) : null}

      {error ? (
        <p className="rounded-lg bg-bad-tint px-4 py-3 text-[13px] text-bad">{error}</p>
      ) : rows === null ? (
        <p className="text-[13.5px] text-muted">Loading…</p>
      ) : rows.length === 0 ? (
        <p className="rounded-xl border border-line bg-surface px-4 py-8 text-center text-[13.5px] text-muted">
          No messages match those filters.
        </p>
      ) : (
        <div className="overflow-x-auto rounded-xl border border-line bg-surface">
          <table className="w-full border-collapse text-left text-[13px]">
            <thead>
              <tr className="border-b border-line text-[11px] tracking-wide text-faint uppercase">
                {["When", "Message", "To", "Order", "Status"].map((label) => (
                  <th key={label} className="px-4 py-3 font-semibold">
                    {label}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {rows.map((row) => (
                <Row
                  key={row.id}
                  message={row}
                  expanded={open === row.id}
                  onToggle={() => setOpen((current) => (current === row.id ? null : row.id))}
                  onResent={refresh}
                />
              ))}
            </tbody>
          </table>
        </div>
      )}

      <Pager page={page} pages={pages} onChange={setPage} />
    </>
  );
}

function Row({
  message,
  expanded,
  onToggle,
  onResent,
}: {
  message: OutboundMessage;
  expanded: boolean;
  onToggle: () => void;
  onResent: () => void;
}) {
  return (
    <>
      <tr
        onClick={onToggle}
        className={cn(
          "cursor-pointer border-b border-line align-top last:border-0 hover:bg-canvas",
          expanded && "bg-canvas",
        )}
      >
        <td className="px-4 py-3 text-[12px] whitespace-nowrap text-muted">
          {formatDateTime(message.createdAt)}
        </td>
        <td className="px-4 py-3">
          <span className="block font-medium text-ink">
            {messages.templateLabel(message.template)}
          </span>
          {message.subject ? (
            <span className="block text-[11.5px] text-faint">{message.subject}</span>
          ) : null}
          {/* A resend is not a separate event to an operator scanning the list —
              it is this message, again. Saying so inline beats a column. */}
          {message.resentFromId ? (
            <span className="mt-0.5 block text-[11px] font-semibold text-info">
              Resent{message.actor ? " by an operator" : ""}
            </span>
          ) : null}
        </td>
        <td className="px-4 py-3 text-[12.5px] break-all text-muted">{message.recipient}</td>
        <td className="px-4 py-3 text-[12px] whitespace-nowrap text-muted">
          {message.orderNumber ?? "—"}
        </td>
        <td className="px-4 py-3">
          <MessageChip status={message.status} />
          {message.error ? (
            <p className="mt-1 max-w-[260px] text-[11.5px] leading-relaxed text-bad">
              {message.error}
            </p>
          ) : null}
        </td>
      </tr>

      {expanded ? (
        <tr className="border-b border-line last:border-0">
          <td colSpan={5} className="bg-canvas px-4 py-4">
            <Detail id={message.id} onResent={onResent} />
          </td>
        </tr>
      ) : null}
    </>
  );
}

// ─── Detail ──────────────────────────────────────────────────────────────────

function Detail({ id, onResent }: { id: string; onResent: () => void }) {
  const [detail, setDetail] = useState<OutboundMessageDetail | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [view, setView] = useState<"html" | "text">("html");
  const [reload, setReload] = useState(0);

  useEffect(() => {
    const controller = new AbortController();

    void messages
      .fetchMessage(id, controller.signal)
      .then((data) => {
        if (!controller.signal.aborted) setDetail(data);
      })
      .catch((thrown: unknown) => {
        if (!controller.signal.aborted) {
          setError(thrown instanceof ApiError ? thrown.message : "Could not load that message.");
        }
      });

    return () => controller.abort();
  }, [id, reload]);

  const refresh = useCallback(() => {
    setReload((n) => n + 1);
    onResent();
  }, [onResent]);

  if (error) return <p className="text-[12.5px] text-bad">{error}</p>;
  if (!detail) return <p className="text-[12.5px] text-muted">Loading…</p>;

  return (
    <div className="flex flex-col gap-4">
      <Resend detail={detail} onResent={refresh} />

      {detail.resends.length > 0 ? (
        <div>
          <p className="mb-1.5 text-[11px] font-bold tracking-wide text-faint uppercase">
            Later attempts
          </p>
          <ul className="flex flex-col gap-1">
            {detail.resends.map((row) => (
              <li key={row.id} className="flex items-center gap-2 text-[12.5px] text-muted">
                <MessageChip status={row.status} />
                <span>{formatDateTime(row.createdAt)}</span>
                <span className="break-all">→ {row.recipient}</span>
              </li>
            ))}
          </ul>
        </div>
      ) : null}

      <div>
        <div className="mb-2 flex items-center gap-3">
          <p className="text-[11px] font-bold tracking-wide text-faint uppercase">Message body</p>

          <div className="flex gap-1">
            {(["html", "text"] as const).map((mode) => (
              <button
                key={mode}
                type="button"
                onClick={() => setView(mode)}
                className={cn(
                  "rounded px-2 py-0.5 text-[11px] font-semibold uppercase",
                  view === mode ? "bg-ink text-white" : "text-muted hover:text-ink",
                )}
              >
                {mode}
              </button>
            ))}
          </div>
        </div>

        {/*
          Re-rendered from the stored inputs on every read — the body itself is
          never stored (§7.3). So this is the mail as the template would produce
          it *today*, which is the right thing to show next to a Resend button
          and the wrong thing to treat as a transcript of what was sent.
        */}
        {detail.rendered ? (
          <>
            <p className="mb-2 text-[11.5px] text-faint">
              Rebuilt from the stored inputs, so it reflects the template as it is now — not a copy
              of the bytes that went out.
            </p>

            {view === "html" ? (
              /*
               * An iframe with `sandbox=""` — no scripts, no forms, no
               * same-origin access. Mail HTML is inert by nature, but this is
               * an operator console rendering content assembled from customer
               * data, and dropping it into the page with dangerouslySetInnerHTML
               * would make a stored-XSS out of a shipping address.
               */
              <iframe
                title="Message preview"
                sandbox=""
                srcDoc={detail.rendered.html}
                className="h-[420px] w-full rounded-lg border border-line bg-white"
              />
            ) : (
              <pre className="max-h-[420px] overflow-auto rounded-lg border border-line bg-surface p-3 text-[11.5px] leading-relaxed whitespace-pre-wrap text-body">
                {detail.rendered.text}
              </pre>
            )}
          </>
        ) : (
          <p className="rounded-lg bg-warn-tint px-3 py-2 text-[12.5px] text-warn">
            This message cannot be rebuilt — template <code>{detail.template}</code> no longer
            renders the inputs stored with it. The record of what was sent is still accurate; only
            the preview and resend are unavailable.
          </p>
        )}
      </div>
    </div>
  );
}

function Resend({ detail, onResent }: { detail: OutboundMessageDetail; onResent: () => void }) {
  const [to, setTo] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [done, setDone] = useState<string | null>(null);

  async function submit() {
    setBusy(true);
    setError(null);
    setDone(null);

    try {
      const { resent } = await messages.resendMessage(detail.id, to.trim() || undefined);

      setDone(
        resent
          ? `Sent again to ${resent.recipient} — recorded as ${resent.status.toLowerCase()}.`
          : "Resent, but the log row could not be read back.",
      );
      setTo("");
      onResent();
    } catch (thrown) {
      setError(thrown instanceof ApiError ? thrown.message : "Could not resend that message.");
    } finally {
      setBusy(false);
    }
  }

  if (!detail.rendered) return null;

  return (
    <div className="rounded-lg border border-line bg-surface p-3">
      <p className="text-[11px] font-bold tracking-wide text-faint uppercase">Resend</p>

      <div className="mt-2 flex flex-wrap items-center gap-2">
        <input
          type="email"
          value={to}
          onChange={(event) => setTo(event.target.value)}
          placeholder={detail.recipient}
          className="w-[280px] rounded-lg border border-line px-3 py-1.5 text-[13px] text-ink placeholder:text-faint"
        />
        <button
          type="button"
          disabled={busy}
          onClick={() => void submit()}
          className="rounded-lg bg-ink px-3 py-1.5 text-[12.5px] font-semibold text-white disabled:opacity-50"
        >
          {busy ? "Sending…" : "Send again"}
        </button>
      </div>

      <p className="mt-2 text-[11.5px] text-faint">
        Leave the address blank to send to {detail.recipient}. Each resend is recorded as its own
        row against this one — pressing it twice sends twice.
      </p>

      {done ? <p className="mt-2 text-[12px] font-semibold text-good">{done}</p> : null}
      {error ? <p className="mt-2 text-[12px] font-semibold text-bad">{error}</p> : null}
    </div>
  );
}
